import { OptionItemObject } from '../../../../../../src/shared_models/interfaces';
import {
    ADD_OPTION_CLICKED,
    DEFAULT_VALUE_CHANGED,
    OPTION_EXPANDED,
    REMOVE_ALL_OPTIONS,
    REMOVE_ONE_ELEMENT,
    REORDER_OPTIONS,
    REPLACE_ONE_ELEMENT,
    SORT_OPTIONS,
    TOGGLE_STATE_CHANGED,
    TRANSLATION_KEY,
    TRANSLATION_VALUE_CHANGED,
    VALUE_CHANGED,
    VALUE_UPDATE,
} from '../../../utils/constants';

import { OptionsSortingData, OptionTranslationValue } from './interfaces';
import { OptionAdderOnChangeTypes, OptionsAdderAllowedValueTypes } from './types';

const addNewEmptyOption = (options: OptionItemObject[]): {updatedOptions: OptionItemObject[]; newOptionKey: string} => {

    const updatedOptions = [...options];
    const key = self.crypto.randomUUID();
    
    updatedOptions.push({
        value: '',
        isDefaultOption: false,
        isEnabled: true,
        key,
        children: [],
        depth: 0,
        translation: {},
    });

    return {updatedOptions, newOptionKey: key};
};

export const updateSingleOption = (
    options: OptionItemObject[],
    value: OptionsAdderAllowedValueTypes,
    optionIndex: number,
    isValueUpdate: boolean,
    isTranslationUpdate = false,
): OptionItemObject[] => {
    const updatedOptions = [...options];
    const optionToUpdate = options[optionIndex];

    if (isValueUpdate) {
        optionToUpdate.value = value as string;
    } else if (isTranslationUpdate) {
        const optionTranslationData = value as OptionTranslationValue;

        if (!optionToUpdate.translation) {
            optionToUpdate.translation = {};
        }

        if (optionTranslationData.language) {
            optionToUpdate[TRANSLATION_KEY][optionTranslationData.language] = optionTranslationData.translation;
        }
    } else {
        optionToUpdate.isEnabled = value as boolean;
    }

    updatedOptions.splice(optionIndex, REPLACE_ONE_ELEMENT, optionToUpdate);

    return updatedOptions;
};

export const sortOptionsByGivenDirection = (sortingData: OptionsSortingData): OptionItemObject[] => {
    const sortedOptions = [...sortingData.options];

    return sortedOptions.sort((firstItem, secondItem) => sortingData.isSortedAsc
        ? secondItem.value.localeCompare(firstItem.value)
        : firstItem.value.localeCompare(secondItem.value));
};

export const removeOption = (currentOptions: OptionItemObject[], optionIndex: number): OptionItemObject[] => {
    const updatedOptions = [...currentOptions];
    updatedOptions.splice(optionIndex, REMOVE_ONE_ELEMENT);

    return updatedOptions;
};

export const updateOptionsAdder = (
    value: OptionsAdderAllowedValueTypes,
    updateOption: OptionAdderOnChangeTypes,
    currentOptions: OptionItemObject[],
    setter: (updatedOptions: OptionItemObject[]) => void,
    optionIndex: number,
): void | string => {
    switch (updateOption) {
    case VALUE_CHANGED:
        setter(updateSingleOption(currentOptions, value, optionIndex, VALUE_UPDATE));
        break;
    case TOGGLE_STATE_CHANGED:
        setter(updateSingleOption(currentOptions, value, optionIndex, !VALUE_UPDATE));
        break;
    case ADD_OPTION_CLICKED:
        const {updatedOptions, newOptionKey} = addNewEmptyOption(currentOptions);
        setter(updatedOptions);
        return newOptionKey;
    case SORT_OPTIONS:
        setter(sortOptionsByGivenDirection(value as OptionsSortingData));
        break;
    case REORDER_OPTIONS:
        setter(value as OptionItemObject[]);
        break;
    case REMOVE_ALL_OPTIONS:
        setter(value as OptionItemObject[]);
        break;
    case OPTION_EXPANDED:
    case DEFAULT_VALUE_CHANGED:
    case TRANSLATION_VALUE_CHANGED:
        break;
    default:
        console.error(`Invalid update option: ${updateOption}`);
    }
};

export const isAddOptionButtonClickable = (currentOptions: OptionItemObject[]): boolean => currentOptions.length === 0
    || !!currentOptions[currentOptions.length - 1].value;

export const reorderDraggableElements = (options: OptionItemObject[], startIndex: number, endIndex: number) => {
    const result = Array.from(options);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
};

export const handleIdToClipboardCopy = (id: string) => {
    const fakeInput = document.createElement("input");
    fakeInput.value = id;
    document.body.appendChild(fakeInput);
    fakeInput.select();
    document.execCommand("copy");
    document.body.removeChild(fakeInput);
};

export const copyArrayWithoutReference = (arrayToCopy: any[]) => JSON.parse(JSON.stringify(arrayToCopy));
